import React, { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useForm } from 'react-hook-form'
import Drawer from '@groovehq/internal-design-system/lib/components/Drawer/UnmanagedDrawer'
import ModalBtns from '@groovehq/internal-design-system/lib/components/ModalBtns/ModalBtns'
import Tabbed from 'subapps/settings/components/drawers/Tabbed'
import { useFetchIntegrationProvider } from 'ducks/integrations/hooks'
import { selectPendingIntegrationInstallStateProviderValueById } from 'ducks/integrations/selectors/settings'
import { doUpdateShopifyStoreSettings } from 'ducks/integrations/operations/settings'
import { omit } from 'util/objects'
import { useRhfDirtyHold, useSimpleDrawerConfirmHolds } from 'util/dirtyHolds'
import Configure from './Configure'
import AdvancedConfigure from './AdvancedConfigure'

const tabsData = [
  {
    key: 'configure',
    tabName: 'Configure',
    component: Configure,
  },
  {
    key: 'advanced',
    tabName: 'Advanced',
    component: AdvancedConfigure,
  },
]

export default function ShopifyUpdateStore({
  drawerResourceId: integrationId,
  drawerTabId = tabsData[0].key,
  open,
  onClose,
  onExit,
  drawerId,
  drawer,
  updateDrawerParameter,
  ...rest
}) {
  const dispatch = useDispatch()

  const { isLoading, hasError, isMissing } = useFetchIntegrationProvider(
    'SHOPIFY_V2'
  )

  const store = useSelector(state =>
    selectPendingIntegrationInstallStateProviderValueById(state, integrationId)
  )

  const isNoResultFound = !store || isMissing

  const useFormMethods = useForm({
    mode: 'all',
  })

  const {
    handleSubmit,
    formState: { isValid, isDirty },
    control,
  } = useFormMethods

  const { releaseHold, holdKey } = useRhfDirtyHold(drawerId, control)

  const onSubmit = useCallback(
    data => {
      const mailboxes = Object.entries(data.mailboxes).reduce(
        (obj, [key, value]) => {
          // eslint-disable-next-line no-param-reassign
          obj[`mailboxes.${key}`] = value

          return obj
        },
        {}
      )

      const entity = {
        ...store,
        settings: {
          settings: {
            ...omit(['mailboxes'], data),
            ...mailboxes,
          },
        },
      }

      dispatch(doUpdateShopifyStoreSettings(store.id, entity))
      releaseHold()
      onClose()
    },
    [store, dispatch, onClose, releaseHold]
  )

  const DrawerForm = useCallback(
    subProps => <form onSubmit={handleSubmit(onSubmit)} {...subProps} />,
    [handleSubmit, onSubmit]
  )

  const handleTabKeyChange = useCallback(
    key => {
      if (key === drawerTabId) return
      updateDrawerParameter('drawerTabId', key)
    },
    [drawerTabId, updateDrawerParameter]
  )

  const [handleOnClose, handleOnExit] = useSimpleDrawerConfirmHolds(
    holdKey,
    onClose,
    onExit
  )

  return (
    <Drawer
      {...rest}
      onClose={handleOnExit}
      size="narrow"
      title="Configure Shopify store"
      open={open}
      isLoading={isLoading}
      isError={hasError}
      isNoResultFound={isNoResultFound}
      footer={
        <ModalBtns
          tertiaryBtnText="Cancel"
          onClickTertiaryBtn={handleOnClose}
          saveBtnDisabled={
            !isValid || !isDirty || isNoResultFound || isLoading || hasError
          }
          saveBtnText="Save"
          saveBtnHtmlType="submit"
        />
      }
      container={DrawerForm}
    >
      <Tabbed
        tabsData={tabsData}
        activeTabKey={drawerTabId}
        onTabKeyChange={handleTabKeyChange}
        store={store}
        {...useFormMethods}
      />
    </Drawer>
  )
}
